const prefix = '_asyncComputed$'

const AsyncComputedMixin = {
    data() {
        return {
            _asyncComputed: {},
        }
    },
    computed: {
        $asyncComputed() {
            return this.$data._asyncComputed
        },
    },
    beforeCreate() {
        const asyncComputed = this.$options.asyncComputed || {}

        if (!Object.keys(asyncComputed).length) {
            return
        }

        const properties = {}

        for (const [key, item] of Object.entries(asyncComputed)) {
            const property = asyncComputedProperty(key, item)
            properties[key] = property
            this.$options.computed[prefix + key] = property.getter
        }

        this.$options.data = createDataInitializationFunction(this.$options, properties)
    },
    created() {
        if (!this.$data._asyncComputed) {
            return
        }

        for (const [key, item] of Object.entries(this.$data._asyncComputed)) {
            this[key] = item.default.call(this)
            this.$watch(prefix + key, item.watcher.bind(this), { immediate: true })
        }
    },
}

const handler = console.error.bind(console, 'Error evaluating async computed property:')

function createDataInitializationFunction(options, asyncComputed) {
    const optionData = options.data

    return function (vm) {
        const data = (typeof optionData === 'function' ? optionData.call(this, vm) : optionData) ?? {}

        data._asyncComputed = asyncComputed

        for (const [key, item] of Object.entries(asyncComputed)) {
            data[key] = item.default.call(this)
        }

        return data
    }
}

export default {
    install(Vue) {
        Vue.config.optionMergeStrategies.asyncComputed = Vue.config.optionMergeStrategies.computed
        Vue.mixin(AsyncComputedMixin)
    },
}

function asyncComputedProperty(key, options) {
    let abort = new AbortController()

    const state = {
        version: 0,
        abort() {
            abort.abort()
        },
        error: false,
        exception: null,
        status: 'updating',
        updating: true,
    }

    state.default =
        options.default && typeof options.default === 'function'
            ? options.default
            : function () {
                  return options.default
              }

    const getter = typeof options === 'function' ? options : options.get

    state.getter = function () {
        state.version

        abort.abort()
        abort = new AbortController()
        return getter.call(this, abort.signal)
    }

    state.update = function () {
        state.version++
    }

    let promiseId = 0

    state.watcher = function (newPromise) {
        /* this is the watcher that watches the computed property we created above */
        const thisPromise = ++promiseId

        if (!newPromise || !newPromise.then) {
            newPromise = Promise.resolve(newPromise)
        }

        state.status = 'updating'
        state.updating = true
        state.error = false

        newPromise
            .then(value => {
                // wrong version of the promise; bail
                if (thisPromise !== promiseId) return

                // update the data value and the state
                this[key] = value

                state.status = 'success'
                state.updating = false
            })
            .catch(err => {
                if (thisPromise !== promiseId) return

                state.exception = err
                state.error = true
                state.updating = false

                handler(err, this, err.stack)
            })
    }

    return state
}
